// src/utils/tileLock.js
import { Preferences } from "@capacitor/preferences";

const KEY = "tile_lock_v1";

// ✅ TEST : 1 heure
// Plus tard : 12h = 12 * 60 * 60 * 1000 ; 24h = 24 * 60 * 60 * 1000
export const LOCK_MS = 60 * 60 * 1000; // 1 heure


function normalizeId(id) {
  // évite les bugs "2" vs 2
  return id === null || id === undefined ? null : String(id);
}

function safeNumber(n, fallback = 0) {
  const x = Number(n);
  return Number.isFinite(x) ? x : fallback;
}

export async function getLockState() {
  const { value } = await Preferences.get({ key: KEY });

  if (!value) {
    return { activeTileId: null, activeUntil: 0, lastOpenedAt: 0 };
  }

  try {
    const parsed = JSON.parse(value);

    return {
      activeTileId: normalizeId(parsed?.activeTileId),
      activeUntil: safeNumber(parsed?.activeUntil, 0),
      lastOpenedAt: safeNumber(parsed?.lastOpenedAt, 0),
    };
  } catch {
    return { activeTileId: null, activeUntil: 0, lastOpenedAt: 0 };
  }
}

export async function setLockState(state) {
  const safeState = {
    activeTileId: normalizeId(state?.activeTileId),
    activeUntil: safeNumber(state?.activeUntil, 0),
    lastOpenedAt: safeNumber(state?.lastOpenedAt, 0),
  };

  await Preferences.set({ key: KEY, value: JSON.stringify(safeState) });
}

export function remainingMs(state, now = Date.now()) {
  const lastOpenedAt = safeNumber(state?.lastOpenedAt, 0);
  return Math.max(0, lastOpenedAt + LOCK_MS - now);
}

/**
 * ✅ Règle:
 * - Si la case cliquée est la case active et pas expirée -> OK (on peut revenir/modifier)
 * - Sinon, si le délai global n’est pas passé -> BLOQUÉ
 * - Sinon -> OK (et elle deviendra la nouvelle case active)
 */
export function canOpenTile(tileId, state, now = Date.now()) {
  const s = state || { activeTileId: null, activeUntil: 0, lastOpenedAt: 0 };

  const clicked = normalizeId(tileId);
  const active = normalizeId(s.activeTileId);
  const activeUntil = safeNumber(s.activeUntil, 0);

  // ✅ Toujours autoriser la case active tant qu'elle n'a pas expiré
  const isSameActive = active && clicked && active === clicked && now < activeUntil;
  if (isSameActive) return { ok: true, reason: "active_tile", waitMs: 0 };

  // ❌ Sinon, on bloque jusqu'à la fin du délai
  const wait = remainingMs(s, now);
  if (wait > 0) return { ok: false, reason: "cooldown", waitMs: wait };

  // ✅ Délai fini : OK pour ouvrir une nouvelle case
  return { ok: true, reason: "new_tile", waitMs: 0 };
}

export async function markTileOpened(tileId, now = Date.now()) {
  const newState = {
    activeTileId: normalizeId(tileId),
    // ✅ “case active” jusqu’à la fin du délai => on peut modifier pendant LOCK_MS
    activeUntil: now + LOCK_MS,
    lastOpenedAt: now,
  };

  await setLockState(newState);
  return newState;
}

export function formatWait(ms) {
  const totalSec = Math.ceil(ms / 1000);
  const min = Math.floor(totalSec / 60);
  const sec = totalSec % 60;
  if (min <= 0) return `${sec}s`;
  return `${min}m ${sec}s`;
}
